// One deliberate library change owns its immediate hydration and rollback.
// Older sessionStorage journals are ignored; nothing is resumed after login.
export function createLibraryTransition(deps) {
    let active = null;
    const failed = error => deps.deferred().reject(error).promise();
    const attempt = callback => { try { return callback(); } catch (error) { return failed(error); } };
    function change(target) {
        if (active) return active.promise;
        const dfd = deps.deferred(), previous = deps.current();
        const entry = { promise: dfd.promise(), owner: undefined, finish: null };
        active = entry;
        const current = () => deps.isCurrent(entry.owner);
        const finish = (error, recovered) => {
            // A late callback must never release a newer operation's fences.
            if (active !== entry) return;
            active = null;
            if (!current()) { deps.abandon(); return dfd.reject({ cancelled: true }); }
            deps.busy(false, !error || recovered);
            if (error) dfd.reject({ ...error, cause: error, libraryRecovered: recovered, libraryUnconfirmed: !recovered });
            else dfd.resolve({ library: target });
        };
        entry.finish = finish;
        try { entry.owner = deps.fence(); }
        catch (error) { active = null; deps.abandon(); dfd.reject({ libraryUnconfirmed: true, cause: error }); return entry.promise; }
        deps.busy(true);
        const recover = error => {
            if (error?.cancelled) return finish(error, false);
            if (!current()) return finish({ cancelled: true }, false);
            // Reconcile an ambiguous result before the single rollback attempt.
            // Never replay the uncertain target write or retry a failed step.
            attempt(() => deps.context()).then(() => {
                if (!current()) return finish({ cancelled: true }, false);
                const effective = deps.current();
                const restore = effective === previous ? deps.resolved() : attempt(() => deps.request(previous, effective));
                restore.then(() => current() ? attempt(() => deps.load(previous)) : failed({ cancelled: true }))
                    .then(() => finish(error, true)).fail(recoveryError => finish({ ...error, recoveryCause: recoveryError }, false));
            }).fail(recoveryError => finish({ ...error, recoveryCause: recoveryError }, false));
        };
        attempt(() => deps.request(target, previous)).then(() => {
            if (!current()) return failed({ cancelled: true });
            return attempt(() => deps.load(target));
        }).then(() => finish(null, true)).fail(recover);
        return entry.promise;
    }
    return { change, pending: () => !!active, cancelObsolete: () => {
        if (active && !deps.isCurrent(active.owner)) active.finish({ cancelled: true }, false);
    } };
}
